import { createContext, Dispatch, PropsWithChildren, useContext, useMemo, useReducer } from "react";
import { useSearchParams } from "react-router";
import { ArticleType } from "@shared/url";
import {
  AgeCategory,
  AnnualAccommodationArticleFilterInput,
  AnyArticleFilterInput,
  RentalAccommodationArticleFilterInput,
  SaleAccommodationArticleFilterInput,
  SeasonalAccommodationArticleFilterInput
} from "@shared/graphql";
import { useTools } from "../hooks/tools";
import { parse } from "../tools/search-params/search-criteria";

export type CustomRentalAccommodationFilterInput = Omit<RentalAccommodationArticleFilterInput, "guests">
  & { guests?: Record<AgeCategory, number> };

export type CustomSeasonalAccommodationFilterInput = Omit<SeasonalAccommodationArticleFilterInput, "guests">
  & { guests?: Record<AgeCategory, number> };

export type AnyArticleSearch = CustomRentalAccommodationFilterInput
  | CustomSeasonalAccommodationFilterInput
  | Exclude<AnyArticleFilterInput, RentalAccommodationArticleFilterInput | SeasonalAccommodationArticleFilterInput>;

export type AnySearchKeys = keyof CustomRentalAccommodationFilterInput
| keyof CustomSeasonalAccommodationFilterInput
| keyof AnnualAccommodationArticleFilterInput
| keyof SaleAccommodationArticleFilterInput;

type State = Record<ArticleType, { readonly mainCriteria: AnySearchKeys[], saving: AnyArticleSearch }>;
type Action = { type: ArticleType, payload: AnyArticleSearch };

const SearchContext = createContext<[ State, Dispatch<Action> ] | undefined>(undefined);

const reducer = (state: State, action: Action): State => ({
  ...state,
  [action.type]: { mainCriteria: state[action.type].mainCriteria, saving: action.payload }
});

const useInitialState = () => {

  const [ searchParams ] = useSearchParams();
  const { routingData } = useTools();

  const searchType = useMemo<ArticleType | undefined>(() => routingData("search")?.withSearchParams, []);

  return {
    [ArticleType.RENTAL]: {
      mainCriteria: [ "objectTypes", "period", "guests" ] as AnySearchKeys[],
      saving: searchType === ArticleType.RENTAL
        ? parse(searchParams, searchType)
        : {} as CustomRentalAccommodationFilterInput,
    },
    [ArticleType.SEASONAL]: {
      mainCriteria: [ "objectTypes", "guests" ] as AnySearchKeys[],
      saving: searchType === ArticleType.SEASONAL
        ? parse(searchParams, searchType)
        : {} as CustomSeasonalAccommodationFilterInput,
    },
    [ArticleType.ANNUAL]: {
      mainCriteria: [ "objectTypes", "priceRange", "roomsRange", "surfaceRange" ] as AnySearchKeys[],
      saving: searchType === ArticleType.ANNUAL
        ? parse(searchParams, searchType)
        : {} as AnnualAccommodationArticleFilterInput,
    },
    [ArticleType.SALE]: {
      mainCriteria: [ "objectTypes", "priceRange", "roomsRange", "surfaceRange" ] as AnySearchKeys[],
      saving: searchType === ArticleType.SALE
        ? parse(searchParams, searchType)
        : {} as SaleAccommodationArticleFilterInput,
    }
  };

}

export const useSearchContext = () => {
  const ctx = useContext(SearchContext);
  if (!ctx) throw new Error("useSearchContext must be used within a SearchProvider");
  return ctx;
}

export const SearchProvider = ({ children }: PropsWithChildren) => {

  const initialState = useInitialState();
  const [ state, dispatch ] = useReducer(reducer, initialState);

  return (
    <SearchContext value={[ state, dispatch ]}>
      {children}
    </SearchContext>);

}
